// Purpose-specific prompt context envelope — secret-free, tier-bounded.
import type { PersistencePolicy } from './contracts.js';
import type {
  AnswerStatus,
  CharacterNature,
  ClosedBehavior,
  ConditionalAnswer,
  ConnectedAnswers,
  LocationGrounding,
  LocationType,
  OperatingSchedule,
  OperatingStatus,
  PlotDevice,
  SummaryRecord,
  TimePeriod,
} from '../domain/types.js';

export const PROMPT_SCHEMA_VERSION = '2.0.0';

/** Context tiers — higher tiers include lower when allowed. */
export type ContextTier = 0 | 1 | 2 | 3;

export type PromptPurpose =
  | 'connected_questions'
  | 'assumption'
  | 'world_devices'
  | 'character_options'
  | 'runtime_scene'
  | 'location_summary'
  | 'missed_activity';

export type TextProvider = 'deepseek';
export type ImageProvider = 'gemini';
export type ProviderKind = TextProvider | ImageProvider;

export type SimulationMode = 'creating' | 'resumed' | 'observed';

export type ValidationStatus = 'pending' | 'valid' | 'invalid' | 'fallback';

export type ConfidenceLabel = 'high' | 'medium' | 'low';

export interface TokenBudget {
  inputTokens: number;
  outputTokens: number;
}

export interface FallbackPolicy<T> {
  /** Deterministic local generator when provider unavailable or validation fails. */
  local: () => T;
  /** Retry once with reduced context when validation fails. */
  retryOnValidationError: boolean;
  /** Use local immediately when no API key is configured. */
  noKeyUsesLocal: boolean;
}

export interface WorldContext {
  worldId: string;
  worldVersion: number;
  locationType: LocationType;
  location: LocationGrounding;
  /** Connected step aggregate when available (notes §7). */
  connected?: ConnectedAnswers | null;
  /** Only confirmed or answered (non-stale) answers. */
  answers: Record<string, ConditionalAnswer>;
  timePeriod: TimePeriod;
  schedule: OperatingSchedule;
  assumptions: Array<{
    assumptionId: string;
    fieldPath: string;
    assumption: string;
    accepted: boolean;
    contextSourceIds: string[];
  }>;
}

export interface SimulationContext {
  inWorldIso: string;
  episodeNumber: number;
  operatingStatus: OperatingStatus;
  mode: SimulationMode;
  /** Active device ids for the current episode/day. */
  activeDeviceIds: string[];
  recentEventIds: string[];
  generationSeed?: string;
}

export interface ActorRelationship {
  targetActorId: string;
  familiarity: number;
  affinity: number;
  trust: number;
}

export interface ActorMemory {
  memoryId: string;
  summary: string;
  salience: number;
}

/** Privacy-safe actor slice — no full last names. */
export interface ActorContext {
  actorId: string;
  characterVersion: number;
  /** Raw first name when available — compiled output uses privacySafeName. */
  firstName?: string;
  lastInitial?: string;
  displayName: string;
  species: string;
  sex: 'M' | 'F';
  ageRange: string;
  appearance?: Record<string, string>;
  /**
   * Notes §11 — 11 nature sliders (0–10). Exposed for influence.ts gating:
   * a later pass should call isFamilyActive('nature') only when this is set
   * (actual slider data), not when appearance alone is present.
   */
  nature?: CharacterNature;
  traits?: string[];
  goals?: string[];
  relationships?: ActorRelationship[];
  durableMemories?: ActorMemory[];
}

export interface HistoryEventSlice {
  eventId: string;
  occurredAt: string;
  summary: string;
  actorIds: string[];
  deviceIds: string[];
  observed: boolean;
}

export interface HistoryContext {
  compactSummaries: Array<{
    summaryId: string;
    scope: SummaryRecord['scope'];
    hash?: string;
    text: string;
  }>;
  recentEvents: HistoryEventSlice[];
}

export interface PromptConstraints {
  contentBoundary: 'edgy-but-safe';
  maxOutputLength?: number;
  conversationMinutes?: [number, number];
  forbiddenFields: string[];
  knownFacts: string[];
  confirmedDecisions: string[];
  allowAssumption: boolean;
}

export interface RequestMeta {
  purpose: PromptPurpose;
  schemaVersion: string;
  worldId: string;
  locale: string;
  episodeId?: string;
  characterIds?: string[];
}

export interface ContextProvenanceManifest {
  purpose: PromptPurpose;
  schemaVersion: string;
  worldId: string;
  worldVersion: number;
  answerIds: string[];
  answerHashes: Record<string, string>;
  actorIds: string[];
  actorVersions: Record<string, number>;
  deviceIds: string[];
  eventIds: string[];
  summaryIds: string[];
  provider: ProviderKind | 'local';
  fallbackUsed: boolean;
  validationStatus: ValidationStatus;
  contextTierMax: ContextTier;
  contextTierUsed: ContextTier;
  /** Nested manifests for multi-step or retry flows. */
  children?: ContextProvenanceManifest[];
}

export interface PromptRequest {
  meta: RequestMeta;
  world: WorldContext;
  simulation: SimulationContext;
  actors: ActorContext[];
  history: HistoryContext;
  constraints: PromptConstraints;
  allowedTiers: ContextTier[];
  maxTier: ContextTier;
  budget: TokenBudget;
  fallback: FallbackPolicy<unknown>;
  provenance: ContextProvenanceManifest;
  /** Zod schema identity for structured provider output validation. */
  outputSchemaId: string;
  /** Whether validated output may be persisted and at which layer. */
  persistencePolicy: PersistencePolicy;
  /** Provider must return JSON matching outputSchemaId when true. */
  structuredOutput: boolean;
  /** Compiled instruction block for the provider (no secrets). */
  instructions: string;
}

/** Raw inputs compilers accept — never a database row or provider payload. */
export interface PromptSourceBundle {
  worldId: string;
  worldVersion: number;
  locationType: LocationType;
  location: LocationGrounding;
  connected?: ConnectedAnswers | null;
  answers: Record<string, ConditionalAnswer>;
  timePeriod: TimePeriod;
  schedule: OperatingSchedule;
  assumptions: WorldContext['assumptions'];
  clock: { inWorldIso: string; episodeNumber: number };
  operatingStatus: OperatingStatus;
  mode: SimulationMode;
  activeDevices: PlotDevice[];
  inactiveDevices?: PlotDevice[];
  allActors: ActorContext[];
  selectedActorIds: string[];
  nearbyActorIds?: string[];
  events: HistoryEventSlice[];
  summaries: HistoryContext['compactSummaries'];
  generationSeed?: string;
  locale?: string;
  episodeId?: string;
  /** Required true for world-device generation (post-review confirmed snapshot). */
  confirmedSnapshot?: boolean;
  /** Previously generated world devices to exclude from new generation. */
  priorGeneratedDevices?: PlotDevice[];
}

export const ACCEPTED_ANSWER_STATUSES: ReadonlySet<AnswerStatus> = new Set([
  'confirmed',
  'answered',
  'suggested',
]);

export const STALE_ANSWER_STATUS: AnswerStatus = 'stale';

export type OvernightScheduleHint = {
  overnight: boolean;
  closedBehavior: ClosedBehavior;
};
